import { formatMoney } from "@/lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";

// ---------- Presentational row primitives used by PayPeriodForecast ----------
// These are pure display components (no state, no data fetching). They live
// here so the main forecast file stays focused on data + orchestration.

export function SectionHeader({ palette, open, onToggle, total, sign, totalLabel, subtitle, flag, testId }) {
  const totalStr = sign === "+" ? `+${formatMoney(Math.abs(total))}` : sign === "-" ? `-${formatMoney(Math.abs(total))}` : formatMoney(total);
  // Header is a SELF-CONTAINED outlined card. When the section is closed
  // its corners are fully rounded; when open, the bottom corners square
  // off to visually "attach" to the lighter-shaded subrow container below.
  // The dark-charcoal 1.5px border makes the section grouping unmistakable.
  const rounded = open ? "rounded-t-2xl" : "rounded-2xl";
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative w-full ${palette.bg} border-[1.5px] border-[#2C302E] ${rounded} pl-6 pr-6 py-4 hover:brightness-95 transition-all`}
      data-testid={`${testId}-header`}
    >
      <div className="row-content">
        <div className="flex items-center gap-2 min-w-0 shrink">
          {open ? <ChevronDown size={18} className={palette.label} /> : <ChevronRight size={18} className={palette.label} />}
          <div className="min-w-0 text-left">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Some section palettes provide a shorter `mobileHeader`
                  variant so long titles don't wrap on narrow screens.
                  Rendered via responsive display classes so we don't need
                  a JS-side viewport check. */}
              {palette.mobileHeader ? (
                <>
                  <span className={`sm:hidden font-heading text-lg font-semibold ${palette.label}`}>{palette.mobileHeader}</span>
                  <span className={`hidden sm:inline font-heading text-lg font-semibold ${palette.label}`}>{palette.header}</span>
                </>
              ) : (
                <span className={`font-heading text-lg font-semibold ${palette.label}`}>{palette.header}</span>
              )}
              {flag && (
                <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#7A2E24] text-white">
                  attention
                </span>
              )}
            </div>
            {palette.subheader && (
              <div className={`text-xs mt-0.5 ${palette.label} opacity-80 font-normal`}>{palette.subheader}</div>
            )}
          </div>
        </div>
        <div className="text-right leading-tight">
          {totalLabel && (
            <div className={`text-[10px] font-semibold uppercase tracking-widest ${palette.label} opacity-70`}>
              {totalLabel}
            </div>
          )}
          <span className={`text-lg font-heading font-bold whitespace-nowrap ${palette.amount}`} data-testid={`${testId}-summary-total`}>
            {totalStr}
          </span>
          {subtitle && (
            <div className={`text-[10px] mt-0.5 ${palette.label} opacity-80 whitespace-nowrap`} data-testid={`${testId}-summary-subtitle`}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// Clickable line item — opens a detail modal listing the underlying bills.
// Falls back to a plain (non-clickable) row when there are 0 bills.
export function LinkRow({ label, sublabel, amount, sign, palette, onClick, disabled, testId }) {
  // Pure navigation rows (like the Feb 2026 "View All Bills") pass
  // amount={null} to signal "no meaningful number here" — we then skip
  // the amount column AND the dotted connector line so the row reads as
  // clean navigation, not a stat with a placeholder value.
  const showAmount = amount !== null && amount !== undefined;
  const displayText = showAmount
    ? (sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount))
    : null;
  const clickable = !disabled;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={clickable ? onClick : undefined}
      className={`w-full py-2.5 ${clickable ? "hover:bg-white/60 cursor-pointer" : "cursor-default"} transition-colors`}
      data-testid={testId}
    >
      <div className="row-content row-connector px-6 text-left">
        <div className="min-w-0">
          <div className={`text-sm ${clickable ? `${palette.label} underline decoration-dotted underline-offset-4 font-semibold` : "text-[#2C302E]"}`}>
            {label}
          </div>
          {sublabel && <div className="text-xs text-[#8A918D] mt-0.5 lowercase first-letter:uppercase">{sublabel}</div>}
        </div>
        <span className={`row-connector-line ${showAmount ? "" : "opacity-0"}`} aria-hidden="true" />
        {showAmount ? (
          <div className={`text-sm font-semibold whitespace-nowrap ${palette.amount}`}>{displayText}</div>
        ) : (
          // Empty spacer so the grid keeps the same 3-column shape as
          // amount-bearing rows. Keeps the label column at the exact
          // same left edge across all LinkRow siblings.
          <div className="text-sm font-semibold whitespace-nowrap opacity-0 select-none" aria-hidden="true">—</div>
        )}
      </div>
    </button>
  );
}

export function SubRow({ label, sublabel, amount, sign, palette, testId }) {
  const displayText = sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount);
  return (
    <div className="py-1.5 bg-white/40" data-testid={testId}>
      <div className="row-content row-connector px-6">
        <div>
          <div className="text-[#59605C] text-xs italic">{label}</div>
          {sublabel && <div className="text-[10px] text-[#8A918D] mt-0.5">{sublabel}</div>}
        </div>
        <span className="row-connector-line" aria-hidden="true" />
        <div className={`text-xs font-medium whitespace-nowrap ${palette.amount}`}>{displayText}</div>
      </div>
    </div>
  );
}

// Clickable variant of SubRow — used for "Bills Pending to Clear" rows so
// users can drill into the underlying bills/charges that make up the amount.
// When `tone="warn"` (used for pending-charge rows) the label + amount render
// in dark rose so it's visually distinct from the surrounding cash rows.
export function LinkSubRow({ label, sublabel, amount, sign, palette, onClick, testId, tone, variant }) {
  const displayText = sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount);
  const warn = tone === "warn";
  // When rendered inside the Step-1 balance card (variant="swapped"), the
  // row uses the WIDER outer band so the amount hugs the section's right
  // edge — visually outside the editable balance pill above it. Everywhere
  // else, keep the classic narrower-indented sub-row shape.
  // Mobile: Step-1 rows (both "swapped" and "unassigned-pending") pin the
  // label to far-left and amount to far-right via `.step1-mobile-inline`.
  const isStep1 = variant === "swapped" || variant === "unassigned-pending";
  const containerCls = variant === "swapped"
    ? `row-content row-connector px-6${isStep1 ? " step1-mobile-inline" : ""}`
    : `sub-row-content row-connector${isStep1 ? " step1-mobile-inline" : ""}`;
  const labelWrapCls = variant === "swapped" ? "sm:pl-8" : "";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full py-2 bg-white/40 hover:bg-white/70 text-left transition-colors"
      data-testid={testId}
    >
      <div className={containerCls}>
        <div className={labelWrapCls}>
          <div className={`text-sm font-semibold underline decoration-dotted underline-offset-4 ${warn ? "text-[#8C3A30]" : palette.label}`}>
            {label}
          </div>
          {sublabel && <div className="text-[10px] text-[#8A918D] mt-0.5">{sublabel}</div>}
        </div>
        <span className="row-connector-line" aria-hidden="true" />
        <div className={`text-right text-sm font-semibold whitespace-nowrap ${warn ? "text-[#8C3A30]" : palette.amount}`}>{displayText}</div>
      </div>
    </button>
  );
}

// Bold + larger bottom-of-section total, per user request.
export function TotalRow({ label, amount, sign, palette, testId }) {
  const displayText = sign === "+" ? `+${formatMoney(Math.abs(amount))}` : sign === "-" ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount);
  return (
    <div className="py-3 border-t-2 border-white/80" data-testid={testId}>
      <div className="row-content row-connector px-6">
        <div className={`text-base font-heading font-bold ${palette.label}`}>{label}</div>
        <span className="row-connector-line" aria-hidden="true" />
        <div className={`text-lg font-heading font-bold whitespace-nowrap ${palette.amount}`}>{displayText}</div>
      </div>
    </div>
  );
}

export function MutedRow({ label }) {
  return (
    <div className="py-2.5">
      <div className="row-content px-6 text-sm text-[#8A918D] italic">{label}</div>
    </div>
  );
}
